/**
 * Create assumption ledger entries from extracted guided-flow data.
 */
import type { AssumptionEntry, AssumptionElementType, AssumptionSource } from '../types/marketfitGuided';

function generateId(): string {
  return `asm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function descriptionFromExtracted(
  expectedOutput: string,
  extracted: Record<string, unknown>
): string {
  const val = extracted[expectedOutput] ?? extracted;
  if (typeof val === 'string' && val.trim()) return val.slice(0, 200);
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (typeof first === 'object' && first !== null) {
      const obj = first as Record<string, unknown>;
      const name = obj.name ?? obj.actor_name ?? obj.role;
      if (name) return String(name).slice(0, 200);
      return `${val.length} item(s)`;
    }
  }
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    const ctx = obj.context ?? obj.description;
    if (ctx) return String(ctx).slice(0, 200);
    const keys = Object.keys(obj).filter((k) => obj[k] != null);
    if (keys.length) return keys.slice(0, 3).join(', ');
  }
  return 'Extracted data';
}

function expectedOutputToElementType(expectedOutput: string): AssumptionElementType {
  switch (expectedOutput) {
    case 'situation_boundary':
      return 'situation_constraint';
    case 'situation_value':
      return 'economic_value';
    case 'actors':
      return 'actor';
    case 'emotional_delta':
      return 'emotional_delta';
    case 'commitment_decision':
      return 'commitment';
    default:
      return 'situation_constraint';
  }
}

/**
 * Create assumption entries from extracted data. Maps expected_output to element_type.
 * source: user_stated when from user input, agent_hypothesis when from agent apply.
 * sourceStep: state_id when created, for validation dropdown targeting.
 */
export function createAssumptionsFromExtracted(
  expectedOutput: string,
  extracted: Record<string, unknown>,
  source: AssumptionSource,
  sourceStep?: string
): AssumptionEntry[] {
  const elementType = expectedOutputToElementType(expectedOutput);
  const description = descriptionFromExtracted(expectedOutput, extracted);
  return [
    {
      id: generateId(),
      element_type: elementType,
      description,
      source,
      validation_type: 'none',
      confidence_level: 'low',
      source_step: sourceStep,
    },
  ];
}

/**
 * Update validation and confidence on assumptions created from a given step.
 */
export function updateAssumptionsValidation(
  assumptions: AssumptionEntry[],
  sourceStep: string,
  validationType: AssumptionEntry['validation_type'],
  confidenceLevel: AssumptionEntry['confidence_level']
): AssumptionEntry[] {
  return assumptions.map((a) =>
    a.source_step === sourceStep
      ? { ...a, validation_type: validationType, confidence_level: confidenceLevel }
      : a
  );
}
